import * as THREE from 'https://unpkg.com/three@0.179.1/build/three.module.js';
import { OrbitControls } from 'https://unpkg.com/three@0.179.1/examples/jsm/controls/OrbitControls.js?module';
import { GLTFLoader } from 'https://unpkg.com/three@0.179.1/examples/jsm/loaders/GLTFLoader.js?module';
import { SVGLoader } from 'https://unpkg.com/three@0.179.1/examples/jsm/loaders/SVGLoader.js?module';

if (!customElements.get('three-scene')) {

    class ThreeScene extends HTMLElement {

        constructor() {
            super();

            this.color_options = document.querySelectorAll('.color-option');
            this.sceneAttr = JSON.parse(this.querySelector('script[data-scene-config]').textContent);

            this.particles = null;
            this.originalPositions = null;
            this.explosionTargets = null;
            this.modelGroup = null;

            this.isMorphing = false;
            this.morphPhase = null;
            this.morphStartTime = 0;
            this.morphDuration = 0;
            this.pendingColor = null;

            this.logoGroup = null;
            this.logoScaleTarget = 1;
            this.logoMaterials = [];
            this.isCurrentGradient = false;
            this.isDesktop = window.innerWidth > 768;

            this.mouse = new THREE.Vector2(-999, -999);
            this.currentOffsets = null;

            this.distortionScene = null;
            this.distortionCamera = null;
            this.distortionRenderer = null;
            this.distortionMaterial = null;
            this.distortionValue = { value: 0 };
        }

        applyGradientToParticles(gradientStr) {
            const gradient = this.parseGradient(gradientStr);
            if (!gradient || !this.particles) return;

            const geometry = this.particles.geometry;
            const positions = geometry.attributes.position;
            const count = positions.count;

            const angleRad = (gradient.angle - 90) * (Math.PI / 180);
            const dirX = Math.cos(angleRad);
            const dirY = -Math.sin(angleRad);

            let minProj = Infinity, maxProj = -Infinity;
            for (let i = 0; i < count; i++) {
                const x = this.originalPositions[i * 3];
                const y = this.originalPositions[i * 3 + 1];
                const proj = x * dirX + y * dirY;
                if (proj < minProj) minProj = proj;
                if (proj > maxProj) maxProj = proj;
            }

            const range = maxProj - minProj || 1;

            const colors = new Float32Array(count * 3);
            for (let i = 0; i < count; i++) {
                const x = this.originalPositions[i * 3];
                const y = this.originalPositions[i * 3 + 1];
                const proj = x * dirX + y * dirY;
                const t = (proj - minProj) / range;

                const color = this.getGradientColorAt(gradient.stops, t);
                colors[i * 3] = color.r;
                colors[i * 3 + 1] = color.g;
                colors[i * 3 + 2] = color.b;
            }

            geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

            this.particles.material.vertexColors = true;
            this.particles.material.color.set(0xffffff);
            this.particles.material.needsUpdate = true;
            this.isCurrentGradient = true;

            if (gradient.stops.length > 0) {
                const activeColor = new THREE.Color(gradient.stops[0].color);
                this.logoMaterials.forEach(material => {
                    material.color.copy(activeColor);
                });

                if (this.ambientParticles) {
                    this.ambientParticles.material.color.copy(activeColor);
                    this.ambientParticles.material.needsUpdate = true;
                }
            }
        }

        applySolidColorToParticles(color) {
            if (!this.particles) return;

            const geometry = this.particles.geometry;

            if (geometry.attributes.color) {
                geometry.deleteAttribute('color');
            }

            this.particles.material.vertexColors = false;
            this.particles.material.color.set(color);
            this.particles.material.needsUpdate = true;
            this.isCurrentGradient = false;

            this.logoMaterials.forEach(material => {
                material.color.set(color);
            });

            if (this.ambientParticles) {
                this.ambientParticles.material.color.set(color);
                this.ambientParticles.material.needsUpdate = true;
            }
        }

        connectedCallback() {
            const { particlesColor, sizeParticles, modelObject, textureParticles, textureLogo, atmosphereParticlesCount, enableMouseHoverEffect, layoutTwoColumns, explodeDuration, implodeDuration } = this.sceneAttr;

            this.explodeDuration = explodeDuration || 800;
            this.implodeDuration = implodeDuration || 1000;

            this.initThree({ particlesColor, sizeParticles, modelObject, textureParticles, atmosphereParticlesCount, enableMouseHoverEffect: enableMouseHoverEffect || null, layoutTwoColumns: layoutTwoColumns || null, textureLogo: textureLogo || null });
            this.updateScaleTarget();

            this.handleResize = this.debounce(() => {
                this.updateScaleTarget();
            }, 200);

            if (this.sceneAttr.enableDistortionEffect) {
                this.initCardDistortionEffect();
            }

            window.addEventListener('resize', this.handleResize);

            this.color_options.forEach(option => {

                option.addEventListener('click', (e) => {
                    this.color_options.forEach(opt => {
                        opt.classList.remove('active');
                    });

                    e.currentTarget.classList.add('active');

                    const newColor = e.currentTarget.dataset.color;
                    this.morphToColor(newColor);

                });

            });
        }

        initCardDistortionEffect() {
            const canvas = document.querySelector('#distortion-canvas');
            if (!canvas) return;

            const container = canvas.parentElement;
            const width = container.clientWidth;
            const height = container.clientHeight;

            this.distortionScene = new THREE.Scene();
            this.distortionCamera = new THREE.OrthographicCamera(-width / 2, width / 2, height / 2, -height / 2, 1, 1000);
            this.distortionCamera.position.z = 1;

            this.distortionRenderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
            this.distortionRenderer.setSize(width, height);
            this.distortionRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

            const textureLoader = new THREE.TextureLoader();
            const texture1 = textureLoader.load(this.sceneAttr.distortionImage1);
            const texture2 = textureLoader.load(this.sceneAttr.distortionImage2);
            const dispTexture = textureLoader.load(this.sceneAttr.displacementImage);

            texture1.minFilter = texture2.minFilter = dispTexture.minFilter = THREE.LinearFilter;

            const defaultVertexShader = `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `;

            const defaultFragmentShader = `
                varying vec2 vUv;
                uniform sampler2D texture1;
                uniform sampler2D texture2;
                uniform sampler2D disp;
                uniform float dispFactor;
                uniform float intensity;

                void main() {
                    vec4 dispColor = texture2D(disp, vUv);
                    vec2 distortedPosition1 = vUv + vec2(dispColor.r, dispColor.g) * intensity * dispFactor;
                    vec2 distortedPosition2 = vUv - vec2(dispColor.r, dispColor.g) * intensity * (1.0 - dispFactor);
                    
                    vec4 _texture1 = texture2D(texture1, distortedPosition1);
                    vec4 _texture2 = texture2D(texture2, distortedPosition2);
                    
                    gl_FragColor = mix(_texture1, _texture2, dispFactor);
                }
            `;

            this.distortionMaterial = new THREE.ShaderMaterial({
                uniforms: {
                    dispFactor: { value: 0 },
                    intensity: { value: this.sceneAttr.distortionIntensity || 0.5 },
                    texture1: { value: texture1 },
                    texture2: { value: texture2 },
                    disp: { value: dispTexture }
                },
                vertexShader: defaultVertexShader,
                fragmentShader: defaultFragmentShader,
                transparent: true
            });

            const geometry = new THREE.PlaneGeometry(width, height);
            const mesh = new THREE.Mesh(geometry, this.distortionMaterial);
            this.distortionScene.add(mesh);

            container.addEventListener('mouseenter', () => {
                this.animateDistortion(1);
            });
            container.addEventListener('mouseleave', () => {
                this.animateDistortion(0);
            });
        }

        animateDistortion(targetValue) {
            const startValue = this.distortionMaterial.uniforms.dispFactor.value;
            const startTime = performance.now();
            const duration = 800;

            const step = (now) => {
                const elapsed = now - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const eased = this.easeInOutCubic(progress);

                this.distortionMaterial.uniforms.dispFactor.value = THREE.MathUtils.lerp(startValue, targetValue, eased);
                this.distortionRenderer.render(this.distortionScene, this.distortionCamera);

                if (progress < 1) {
                    requestAnimationFrame(step);
                }
            };
            requestAnimationFrame(step);
        }

        resizeDistortionCanvas() {
            const canvas = document.querySelector('#distortion-canvas');
            if (!canvas || !this.distortionRenderer) return;

            const container = canvas.parentElement;
            const width = container.clientWidth;
            const height = container.clientHeight;

            this.distortionCamera.left = -width / 2;
            this.distortionCamera.right = width / 2;
            this.distortionCamera.top = height / 2;
            this.distortionCamera.bottom = -height / 2;
            this.distortionCamera.updateProjectionMatrix();

            this.distortionRenderer.setSize(width, height);
            this.distortionRenderer.render(this.distortionScene, this.distortionCamera);
        }

        morphToColor(newColor) {
            if (!this.particles || this.isMorphing || !this.originalPositions) return;

            const count = this.originalPositions.length / 3;
            this.explosionTargets = new Float32Array(count * 3);

            for (let i = 0; i < count; i++) {
                const ox = this.originalPositions[i * 3];
                const oy = this.originalPositions[i * 3 + 1];
                const oz = this.originalPositions[i * 3 + 2];

                const dir = new THREE.Vector3(ox, oy, oz);

                if (dir.lengthSq() < 0.0001) {
                    dir.set(
                        Math.random() - 0.5,
                        Math.random() - 0.5,
                        Math.random() - 0.5
                    );
                }

                dir.normalize();

                dir.x += (Math.random() - 0.5) * 0.6;
                dir.y += (Math.random() - 0.5) * 0.6;
                dir.z += (Math.random() - 0.5) * 0.6;
                dir.normalize();

                const distance = this.maxSize * (0.5 + Math.random() * 0.7);

                this.explosionTargets[i * 3] = ox + dir.x * distance;
                this.explosionTargets[i * 3 + 1] = oy + dir.y * distance;
                this.explosionTargets[i * 3 + 2] = oz + dir.z * distance;
            }

            this.pendingColor = newColor;
            this.isMorphing = true;
            this.morphPhase = 'exploding';
            this.morphStartTime = performance.now();
            this.morphDuration = this.explodeDuration;
        }

        updateMorph() {
            if (!this.isMorphing) return;

            const now = performance.now();
            const elapsed = now - this.morphStartTime;
            const t = Math.min(elapsed / this.morphDuration, 1);

            const eased = this.morphPhase === 'exploding' ? this.easeOutCubic(t) : this.easeInOutCubic(t);

            const positions = this.particles.geometry.attributes.position;
            const count = positions.count;

            const from = this.morphPhase === 'exploding' ? this.originalPositions : this.explosionTargets;

            const to = this.morphPhase === 'exploding' ? this.explosionTargets : this.originalPositions;

            for (let i = 0; i < count; i++) {
                const i3 = i * 3;

                positions.setXYZ(i,
                    THREE.MathUtils.lerp(from[i3], to[i3], eased),
                    THREE.MathUtils.lerp(from[i3 + 1], to[i3 + 1], eased),
                    THREE.MathUtils.lerp(from[i3 + 2], to[i3 + 2], eased)
                );
            }

            positions.needsUpdate = true;

            if (this.logoGroup) {

                if (this.morphPhase === 'exploding') {

                    const logoProgress = Math.max(0, (eased - 0.3) / 0.7);
                    const scale = THREE.MathUtils.lerp(0,this.logoScaleTarget,logoProgress);
                    this.logoGroup.scale.set(scale,scale,scale);
                } else {
                    const scale = THREE.MathUtils.lerp(this.logoScaleTarget,0,eased);
                    this.logoGroup.scale.set(scale,scale,scale);
                }
            }

            if (t >= 1) {
                if (this.morphPhase === 'exploding') {
                    if (this.isGradient(this.pendingColor)) {
                        this.applyGradientToParticles(this.pendingColor);
                    } else {
                        this.applySolidColorToParticles(this.pendingColor);
                    }
                    this.morphPhase = 'imploding';
                    this.morphStartTime = now;
                    this.morphDuration = this.implodeDuration;
                } else {
                    this.isMorphing = false;
                    this.morphPhase = null;
                }
            }
        }

        updateHoverEffect(camera) {
            if (!this.particles || !this.originalPositions || this.isMorphing) return;

            const positions = this.particles.geometry.attributes.position;
            const p_pos = positions.array;
            const count = positions.count;

            const time = performance.now() * 0.003;

            const mouse3D = new THREE.Vector3(this.mouse.x, this.mouse.y, 0.5);
            mouse3D.unproject(camera);
            mouse3D.sub(camera.position).normalize();

            const distanceToTarget = -camera.position.z / mouse3D.z;
            const targetMousePos = camera.position.clone().add(mouse3D.multiplyScalar(distanceToTarget));

            const localMouse = targetMousePos.clone();
            this.particles.worldToLocal(localMouse);

            const baseRadius = 3.5;         
            const distortionStrength = 0.6;
            const returnSpeed = 0.05;

            for (let i = 0; i < count; i++) {
                const i3 = i * 3;

                const currentX = p_pos[i3];
                const currentY = p_pos[i3 + 1];
                const currentZ = p_pos[i3 + 2];

                const orgX = this.currentOffsets[i3];
                const orgY = this.currentOffsets[i3 + 1];
                const orgZ = this.currentOffsets[i3 + 2];

                const organicVariation = Math.sin(i * 12.9898 + orgX * 3.0 + orgY * 3.0) * 0.6;
                const effectiveRadius = baseRadius + organicVariation;

                const dx = currentX - localMouse.x;
                const dy = currentY - localMouse.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance < effectiveRadius && distance > 0.0001) {
                    const distortionAmount = (effectiveRadius - distance) * distortionStrength;

                    const floatX = Math.sin(time * 3 + i * 0.5) * 0.03;
                    const floatY = Math.cos(time * 3 + i * 0.3) * 0.03;
                    const floatZ = Math.sin(time * 2 + i) * 0.05;

                    p_pos[i3] += (dx / distance) * distortionAmount * 0.15 + floatX;
                    p_pos[i3 + 1] += (dy / distance) * distortionAmount * 0.15 + floatY;

                    const dz = currentZ - localMouse.z;
                    p_pos[i3 + 2] += (dz / (distance + 0.1)) * distortionAmount * 0.05 + floatZ;

                } else {
                    // Rientro morbido originale
                    p_pos[i3] += (orgX - currentX) * returnSpeed;
                    p_pos[i3 + 1] += (orgY - currentY) * returnSpeed;
                    p_pos[i3 + 2] += (orgZ - currentZ) * returnSpeed;
                }
            }

            positions.needsUpdate = true;
        }

        createAmbientParticles(scene, particleTexture, particleCount, maxSize, colorStart) {
            const count = particleCount;
            const geometry = new THREE.BufferGeometry();
            const positions = new Float32Array(count * 3);
            const velocities = [];

            const range = maxSize * 3;

            for (let i = 0; i < count; i++) {
                positions[i * 3] = (Math.random() - 0.5) * range;
                positions[i * 3 + 1] = (Math.random() - 0.5) * range;
                positions[i * 3 + 2] = (Math.random() - 0.5) * range;

                velocities.push({
                    x: 0.05 + Math.random() * 0.1,
                    y: (Math.random() - 0.5) * 0.02,
                    z: (Math.random() - 0.5) * 0.03,
                    phase: Math.random() * Math.PI * 2
                });
            }

            geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

            const material = new THREE.PointsMaterial({
                color: new THREE.Color(colorStart || 0xffffff),
                size: maxSize * 0.03,
                sizeAttenuation: true,
                map: particleTexture,
                transparent: true,
                opacity: 0.7,
                depthWrite: false,
                blending: THREE.AdditiveBlending
            });

            this.ambientParticles = new THREE.Points(geometry, material);
            this.ambientVelocities = velocities;
            this.ambientRange = range;
            scene.add(this.ambientParticles);
        }

        updateAmbientParticles() {
            if (!this.ambientParticles) return;

            const positions = this.ambientParticles.geometry.attributes.position;
            const count = positions.count;
            const halfRange = this.ambientRange / 2;
            const time = performance.now() * 0.001;

            for (let i = 0; i < count; i++) {
                const vel = this.ambientVelocities[i];

                let x = positions.getX(i) + vel.x;
                let y = positions.getY(i) + Math.sin(time + vel.phase) * 0.05 + vel.y;
                let z = positions.getZ(i) + vel.z;

                if (x > halfRange) x = -halfRange;
                else if (x < -halfRange) x = halfRange;

                if (y > halfRange) y = -halfRange;
                else if (y < -halfRange) y = halfRange;

                if (z > halfRange) z = -halfRange;
                else if (z < -halfRange) z = halfRange;

                positions.setXYZ(i, x, y, z);
            }

            positions.needsUpdate = true;
        }

        updateCameraOffset(camera, sizes, layoutTwoColumns) {
            this.isDesktop = window.innerWidth > 768;

            if (layoutTwoColumns && this.isDesktop) {
                camera.setViewOffset(
                    sizes.width,
                    sizes.height,
                    -sizes.width * 0.18,
                    0,
                    sizes.width,
                    sizes.height
                );
            } else {
                camera.clearViewOffset();
            }
            camera.updateProjectionMatrix();
        }

        initThree(config) {

            const scene = new THREE.Scene();
            const canvas = this.querySelector('#three-canvas');

            const sizes = {
                width: this.clientWidth || window.innerWidth,
                height: this.clientHeight || window.innerHeight
            };

            const camera = new THREE.PerspectiveCamera(
                60, sizes.width / sizes.height, 0.1, 1000
            );

            const renderer = new THREE.WebGLRenderer({
                canvas,
                alpha: true,
                antialias: true
            });

            const ambientLight = new THREE.AmbientLight(0xffffff, 2);
            scene.add(ambientLight);

            const directionalLight = new THREE.DirectionalLight(0xffffff, 2);
            directionalLight.position.set(0, 5, 5);
            scene.add(directionalLight);

            renderer.setSize(sizes.width, sizes.height);
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

            const controls = new OrbitControls(camera, renderer.domElement);
            controls.enableDamping = true;
            controls.dampingFactor = 0.05;
            controls.enableZoom = false;
            controls.enablePan = false;
            controls.rotateSpeed = 0.5;

            this.modelGroup = new THREE.Group();
            scene.add(this.modelGroup);


            canvas.addEventListener('mousemove', (e) => {
                const rect = canvas.getBoundingClientRect();
                this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
                this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            });

            canvas.addEventListener('mouseleave', () => {
                this.mouse.set(-999, -999);
            });

            canvas.addEventListener('mouseleave', () => {
                this.mouse.set(-999, -999);
                this.prevMouse.set(-999, -999);
                this.mouseVelocity.set(0, 0);
            });

            const loader = new GLTFLoader();

            loader.load(
                config.modelObject,
                (gltf) => {
                    const model = gltf.scene;
                    model.updateMatrixWorld(true);
                    const positionsList = [];

                    model.traverse((child) => {
                        if (child.isMesh && child.geometry) {
                            const geom = child.geometry.clone();
                            geom.applyMatrix4(child.matrixWorld);

                            const posAttr = geom.attributes.position;
                            if (posAttr) {
                                for (let i = 0; i < posAttr.count; i++) {
                                    positionsList.push(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
                                }
                            }
                        }
                    });

                    if (positionsList.length === 0) {
                        console.error('mesh not found in the model');
                        return;
                    }

                    const geometry = new THREE.BufferGeometry();
                    const floatPositions = new Float32Array(positionsList);
                    geometry.setAttribute('position', new THREE.BufferAttribute(floatPositions, 3));
                    geometry.center();

                    const textureLoader = new THREE.TextureLoader();
                    const particleTexture = textureLoader.load(config.textureParticles);

                    this.originalPositions = floatPositions.slice();
                    this.currentOffsets = floatPositions.slice();

                    const material = new THREE.PointsMaterial({
                        color: config.particlesColor,
                        size: 0.05,
                        sizeAttenuation: true,
                        map: particleTexture,
                        transparent: true
                    });

                    this.particles = new THREE.Points(geometry, material);
                    this.modelGroup.add(this.particles);

                    const box = new THREE.Box3().setFromObject(this.particles);
                    const size = box.getSize(new THREE.Vector3());
                    const center = box.getCenter(new THREE.Vector3());
                    this.particles.position.sub(center);
                    this.basePosition = {
                        x: this.particles.position.x,
                        y: this.particles.position.y,
                        z: this.particles.position.z
                    };
                    const maxSize = Math.max(size.x, size.y, size.z);
                    this.maxSize = maxSize;

                    const multiplierSize = window.innerWidth <= 768 ? 1.6 : 1.2;
                    camera.position.set(0, 0, maxSize * multiplierSize);
                    this.updateCameraOffset(camera, sizes, config.layoutTwoColumns);
                    controls.target.set(0, 0, 0);
                    controls.update();
                    this.createAmbientParticles(scene, particleTexture, config.atmosphereParticlesCount, maxSize, config.particlesColor);
                },
                undefined,
                (error) => console.error('GLTF error:', error)
            );
            const svgLoader = new SVGLoader();

            config.textureLogo && svgLoader.load(config.textureLogo, (data) => {

                const contentGroup = new THREE.Group();
                data.paths.forEach((path) => {

                    const material = new THREE.MeshStandardMaterial({
                        color: config.particlesColor,
                        side: THREE.DoubleSide,
                        depthWrite: true
                    });

                    this.logoMaterials.push(material);


                    const shapes = SVGLoader.createShapes(path);
                    shapes.forEach((shape) => {

                        const geometry = new THREE.ExtrudeGeometry(shape, {
                            depth: 10,
                            bevelEnabled: true,
                            bevelSize: 0.3,
                            bevelThickness: 0.5,
                            bevelSegments: 5
                        });

                        geometry.computeBoundingBox();
                        const mesh = new THREE.Mesh(geometry,material);
                        contentGroup.add(mesh);
                    });

                });

                contentGroup.rotation.x = Math.PI;

                const box = new THREE.Box3().setFromObject(contentGroup);
                const center = box.getCenter(new THREE.Vector3());

                contentGroup.position.x -= center.x;
                contentGroup.position.y -= center.y;
                contentGroup.position.z -= center.z;

                const outerGroup = new THREE.Group();
                outerGroup.add(contentGroup);
                outerGroup.scale.set(0, 0, 0);

                this.logoGroup = outerGroup;

                this.modelGroup.add(outerGroup);
            });

            const clock = new THREE.Clock();

            const animate = () => {
                requestAnimationFrame(animate);

                const elapsed = clock.getElapsedTime();

                if (this.particles && this.originalPositions && this.basePosition) {
                    if (this.isMorphing) {
                        this.updateMorph();
                    } else {
                        config.enableMouseHoverEffect && this.updateHoverEffect(camera);
                    }
                    config.enableMouseHoverEffect ? this.modelGroup.rotation.y = -0.35 : this.modelGroup.rotation.y = elapsed * 0.15;
                }
                this.updateAmbientParticles();
                controls.update();
                renderer.render(scene, camera);

                if (this.distortionRenderer && this.distortionScene && this.distortionCamera) {
                    this.distortionRenderer.render(this.distortionScene, this.distortionCamera);
                }
            };

            animate();

            this.handleResize = this.debounce(() => {
                const w = this.clientWidth || window.innerWidth;
                const h = this.clientHeight || window.innerHeight;
                camera.aspect = w / h;

                const multiplier = w <= 768 ? 1.6 : 1.2;
                if (this.maxSize) {
                    camera.position.z = this.maxSize * multiplier;
                }
                
                camera.updateProjectionMatrix();
                renderer.setSize(w, h);

                this.updateCameraOffset(
                    camera,
                    {
                        width: w,
                        height: h
                    },
                    config.layoutTwoColumns
                );

            }, 200);

            window.addEventListener('resize', this.handleResize);
        }

        debounce(callback, delay = 200) {
            let timeout;

            return (...args) => {
                clearTimeout(timeout);
                timeout = setTimeout(() => callback.apply(this, args), delay);
            };
        }

        updateScaleTarget() {
            const baseScale = 0.045; 
            this.logoScaleTarget = window.innerWidth <= 768 ? baseScale * 0.75 : baseScale;
        }

        easeOutCubic(t) {
            return 1 - Math.pow(1 - t, 3);
        }

        easeInOutCubic(t) {
            return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        }

        isGradient(colorValue) {
            return typeof colorValue === 'string' && colorValue.includes('gradient');
        }

        parseGradient(gradientStr) {
            const colorRegex = /(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\))\s*(\d+%)?/g;
            const stops = [];
            let match;

            while ((match = colorRegex.exec(gradientStr)) !== null) {
                const color = match[1];
                const position = match[2] ? parseFloat(match[2]) / 100 : null;
                stops.push({ color, position });
            }

            if (stops.length === 0) return null;

            for (let i = 0; i < stops.length; i++) {
                if (stops[i].position === null) {
                    if (i === 0) stops[i].position = 0;
                    else if (i === stops.length - 1) stops[i].position = 1;
                    else stops[i].position = i / (stops.length - 1);
                }
            }

            const angleMatch = gradientStr.match(/(\d+)deg/);
            const angle = angleMatch ? parseFloat(angleMatch[1]) : 180;

            return { stops, angle };
        }

        getGradientColorAt(stops, t) {
            t = Math.max(0, Math.min(1, t));

            if (t <= stops[0].position) return new THREE.Color(stops[0].color);
            if (t >= stops[stops.length - 1].position) return new THREE.Color(stops[stops.length - 1].color);

            for (let i = 0; i < stops.length - 1; i++) {
                if (t >= stops[i].position && t <= stops[i + 1].position) {
                    const localT = (t - stops[i].position) / (stops[i + 1].position - stops[i].position);
                    const c1 = new THREE.Color(stops[i].color);
                    const c2 = new THREE.Color(stops[i + 1].color);
                    return c1.lerp(c2, localT);
                }
            }
            return new THREE.Color(stops[0].color);
        }
    }

    customElements.define('three-scene', ThreeScene);
}